import { useState, useEffect, useMemo } from 'react';
import { UserPlus, Info, Loader2, Lightbulb, Building2, Globe, Eye, EyeOff, Link as LinkIcon, Server, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import { ExternalVpostSetupForm } from './ExternalVpostSetupForm';
import { generateOcpiSuggestions } from '../../utils/ocpi-party-generator';
import type { ExternalCpoConnectionRequestDto, VpostConnectionRequestDto, OcpiVersion, CpoType } from '../../types/api-types';

interface OnboardingFormProps {
  operatorName: string;
  currency?: string;
  onSubmitInternal: (partyId: string, countryCode: string, name: string) => Promise<void>;
  onSubmitExternal: (data: ExternalCpoConnectionRequestDto) => Promise<void>;
  onSubmitExternalVpost: (data: VpostConnectionRequestDto) => Promise<void>;
  onLoadCsmsNames: () => Promise<string[]>;
  onLoadKioskCsmsNames: () => Promise<string[]>;
  isLoadingInternal?: boolean;
  isLoadingExternal?: boolean;
  isLoadingExternalVpost?: boolean;
  error?: string | null;
}

const OCPI_VERSIONS: OcpiVersion[] = ['2.2.1', '2.2', '2.1.1'];

/**
 * Form for onboarding an operator to OCPI
 * First step: Choose CPO type (Internal or External)
 * Second step: Fill in the appropriate form based on selection
 */
export function OnboardingForm({
  operatorName,
  currency,
  onSubmitInternal,
  onSubmitExternal,
  onSubmitExternalVpost,
  onLoadCsmsNames,
  onLoadKioskCsmsNames,
  isLoadingInternal,
  isLoadingExternal,
  isLoadingExternalVpost,
  error
}: OnboardingFormProps) {
  // CPO type selection
  const [cpoType, setCpoType] = useState<CpoType | null>(null);
  // External CPO connection type selection (kiosk or vpost)
  const [connectionType, setConnectionType] = useState<'kiosk' | 'vpost' | null>(null);

  // Internal CPO form fields
  const suggestions = useMemo(
    () => generateOcpiSuggestions(operatorName, currency),
    [operatorName, currency]
  );
  const [partyId, setPartyId] = useState(suggestions.partyId);
  const [countryCode, setCountryCode] = useState(suggestions.countryCode);
  const [name, setName] = useState(operatorName);

  // External CPO form fields
  const [versionsUrl, setVersionsUrl] = useState('');
  const [tokenA, setTokenA] = useState('');
  const [connectionVersion, setConnectionVersion] = useState<OcpiVersion>('2.2.1');
  const [csmsName, setCsmsName] = useState('');
  const [showTokenA, setShowTokenA] = useState(false);

  const [csmsNames, setCsmsNames] = useState<string[]>([]);
  const [csmsNamesLoading, setCsmsNamesLoading] = useState(false);
  const [csmsNamesError, setCsmsNamesError] = useState<string | null>(null);
  const [csmsRetryKey, setCsmsRetryKey] = useState(0);

  // Validation errors
  const [partyIdError, setPartyIdError] = useState<string | null>(null);
  const [countryCodeError, setCountryCodeError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [versionsUrlError, setVersionsUrlError] = useState<string | null>(null);
  const [tokenAError, setTokenAError] = useState<string | null>(null);
  const [csmsNameError, setCsmsNameError] = useState<string | null>(null);

  // Update form when operator changes
  useEffect(() => {
    setPartyId(suggestions.partyId);
    setCountryCode(suggestions.countryCode);
    setName(operatorName);
  }, [operatorName, suggestions.partyId, suggestions.countryCode]);

  // Detect Party ID conflict errors from API (409 Conflict)
  useEffect(() => {
    if (error && error.toLowerCase().includes('party id') && error.toLowerCase().includes('already in use')) {
      setPartyIdError('This Party ID is already in use. Please choose a different one.');
    }
  }, [error]);

  // Load CSMS list for the Kiosk form (GET /ocpi-admin/csms)
  useEffect(() => {
    if (connectionType !== 'kiosk') return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    setCsmsNamesLoading(true);
    setCsmsNamesError(null);

    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('timeout')), 10000);
    });

    Promise.race([onLoadKioskCsmsNames(), timeout])
      .then((names) => {
        if (cancelled) return;
        // Radix SelectItem throws on empty-string values
        const validNames = (names ?? []).filter((name) => name?.trim());
        if (validNames.length === 0) {
          setCsmsNamesError('No CSMS available. Please try again later.');
        } else {
          setCsmsNames(validNames);
        }
        setCsmsNamesLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCsmsNamesError('Failed to load CSMS list. Please try again later.');
        setCsmsNamesLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [connectionType, onLoadKioskCsmsNames, csmsRetryKey]);

  // Internal CPO validation
  const validatePartyId = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) {
      setPartyIdError('Party ID is required');
      return false;
    }
    if (trimmed.length !== 3) {
      setPartyIdError('Must be exactly 3 characters');
      return false;
    }
    if (!/^[A-Z]{3}$/.test(trimmed)) {
      setPartyIdError('Must contain only letters (A-Z)');
      return false;
    }
    setPartyIdError(null);
    return true;
  };

  const validateCountryCode = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) {
      setCountryCodeError('Country code is required');
      return false;
    }
    if (trimmed.length !== 2) {
      setCountryCodeError('Must be exactly 2 characters');
      return false;
    }
    if (!/^[A-Z]{2}$/.test(trimmed)) {
      setCountryCodeError('Must contain only letters (A-Z)');
      return false;
    }
    setCountryCodeError(null);
    return true;
  };

  const validateName = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setNameError('Name is required');
      return false;
    }
    if (trimmed.length < 2) {
      setNameError('Must be at least 2 characters');
      return false;
    }
    setNameError(null);
    return true;
  };

  // External CPO validation
  const validateVersionsUrl = (value: string): boolean => {
    if (!value.trim()) {
      setVersionsUrlError('Versions URL is required');
      return false;
    }
    try {
      new URL(value.trim());
      setVersionsUrlError(null);
      return true;
    } catch {
      setVersionsUrlError('Must be a valid URL');
      return false;
    }
  };

  const validateTokenA = (value: string): boolean => {
    if (!value.trim()) {
      setTokenAError('Token A is required');
      return false;
    }
    setTokenAError(null);
    return true;
  };

  const validateCsmsName = (value: string): boolean => {
    if (!value.trim()) {
      setCsmsNameError('CSMS selection is required');
      return false;
    }
    setCsmsNameError(null);
    return true;
  };

  const handleSubmitInternal = async (e: React.FormEvent) => {
    e.preventDefault();

    const isPartyIdValid = validatePartyId(partyId);
    const isCountryCodeValid = validateCountryCode(countryCode);
    const isNameValid = validateName(name);

    if (!isPartyIdValid || !isCountryCodeValid || !isNameValid) {
      return;
    }

    await onSubmitInternal(partyId.trim().toUpperCase(), countryCode.trim().toUpperCase(), name.trim());
  };

  const handleSubmitExternal = async (e: React.FormEvent) => {
    e.preventDefault();

    const isVersionsUrlValid = validateVersionsUrl(versionsUrl);
    const isTokenAValid = validateTokenA(tokenA);
    const isCsmsNameValid = validateCsmsName(csmsName);

    if (!isVersionsUrlValid || !isTokenAValid || !isCsmsNameValid) {
      return;
    }

    await onSubmitExternal({
      connection_version: connectionVersion,
      versions_url: versionsUrl.trim(),
      token_a: tokenA.trim(),
      csms_name: csmsName.trim(),
    });
  };

  // CSMS list failed to load — block Kiosk form submission
  const csmsUnavailable = !csmsNamesLoading && !!csmsNamesError;

  // Step 1: CPO Type Selection
  if (cpoType === null) {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserPlus className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg text-blue-900">OCPI Onboarding Required</CardTitle>
              <CardDescription className="text-blue-700">
                This operator is not yet configured for OCPI. Please choose your connection type.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Information Box */}
          <div className="bg-white rounded-lg border border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                Choose how this operator will connect to the OCPI network. This is a <strong>one-time choice</strong> and cannot be changed later.
              </p>
            </div>
          </div>

          {/* CPO Type Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Internal CPO Option */}
            <button
              onClick={() => setCpoType('internal')}
              className="flex flex-col items-start p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-green-600" />
                </div>
                <span className="font-semibold text-gray-900">Internal CPO</span>
              </div>
              <p className="text-sm text-gray-600">
                Nayax manages your charging infrastructure. You'll provide your organization's OCPI party details (Party ID, Country Code, Name).
              </p>
              <span className="mt-3 text-xs text-green-600 font-medium">Recommended for most operators</span>
            </button>

            {/* External CPO Option */}
            <button
              onClick={() => setCpoType('external')}
              className="flex flex-col items-start p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Globe className="h-5 w-5 text-purple-600" />
                </div>
                <span className="font-semibold text-gray-900">External CPO</span>
              </div>
              <p className="text-sm text-gray-600">
                You have your own OCPI-compliant system. Connect via external endpoint with your OCPI versions URL and authentication token.
              </p>
              <span className="mt-3 text-xs text-purple-600 font-medium">For operators with existing OCPI infrastructure</span>
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Step 2a: Internal CPO Form
  if (cpoType === 'internal') {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Building2 className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-lg text-blue-900">Internal CPO Setup</CardTitle>
              <CardDescription className="text-blue-700">
                Configure your organization's OCPI party details.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmitInternal} className="space-y-6">
            {/* Back Button */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCpoType(null)}
              className="text-blue-600 hover:text-blue-700 -ml-2"
            >
              ← Back to type selection
            </Button>

            {/* Information Box */}
            <div className="bg-white rounded-lg border border-blue-200 p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    <strong>Party ID:</strong> A unique 3-letter identifier for your organization in the OCPI network.
                  </p>
                  <p>
                    <strong>Country Code:</strong> The ISO 3166-1 alpha-2 country code where your organization is registered.
                  </p>
                  <p>
                    <strong>Name:</strong> The display name for your organization that will be shown to other OCPI parties.
                  </p>
                </div>
              </div>
            </div>

            {/* Suggestion Notice */}
            {(suggestions.partyId || suggestions.countryCode) && (
              <div className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    Values have been auto-suggested based on operator details
                    {currency && <span className="text-amber-600"> (currency: {currency})</span>}.
                  </p>
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Party ID */}
              <div className="space-y-2">
                <Label htmlFor="partyId" className="text-gray-700">
                  Party ID <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="partyId"
                  value={partyId}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().slice(0, 3);
                    setPartyId(value);
                    if (partyIdError) validatePartyId(value);
                  }}
                  placeholder="ABC"
                  maxLength={3}
                  className={`uppercase ${partyIdError ? 'border-red-500' : ''}`}
                  disabled={isLoadingInternal}
                />
                {partyIdError && <p className="text-xs text-red-600">{partyIdError}</p>}
                <p className="text-xs text-gray-500">3 letters (A-Z)</p>
              </div>

              {/* Country Code */}
              <div className="space-y-2">
                <Label htmlFor="countryCode" className="text-gray-700">
                  Country Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="countryCode"
                  value={countryCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().slice(0, 2);
                    setCountryCode(value);
                    if (countryCodeError) validateCountryCode(value);
                  }}
                  placeholder="US"
                  maxLength={2}
                  className={`uppercase ${countryCodeError ? 'border-red-500' : ''}`}
                  disabled={isLoadingInternal}
                />
                {countryCodeError && <p className="text-xs text-red-600">{countryCodeError}</p>}
                <p className="text-xs text-gray-500">ISO 3166-1 alpha-2 (e.g., US, IL, DE)</p>
              </div>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700">
                Display Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) validateName(e.target.value);
                }}
                placeholder="Your Organization Name"
                className={nameError ? 'border-red-500' : ''}
                disabled={isLoadingInternal}
              />
              {nameError && <p className="text-xs text-red-600">{nameError}</p>}
              <p className="text-xs text-gray-500">This name will be visible to other OCPI parties</p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button type="submit" disabled={isLoadingInternal} className="bg-green-600 hover:bg-green-700 text-white">
                {isLoadingInternal ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <Building2 className="h-4 w-4 mr-2" />
                    Setup Internal CPO
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Step 2b: External CPO — connection type selector (Kiosk vs VPOST)
  if (cpoType === 'external' && connectionType === null) {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Globe className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-lg text-blue-900">External CPO Setup</CardTitle>
              <CardDescription className="text-blue-700">
                Choose how to connect your external OCPI system.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setCpoType(null)}
            className="text-blue-600 hover:text-blue-700 -ml-2"
          >
            ← Back to type selection
          </Button>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Connect to Nayax Kiosk */}
            <button
              type="button"
              onClick={() => setConnectionType('kiosk')}
              className="flex flex-col items-start p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <LinkIcon className="h-5 w-5 text-purple-600" />
                </div>
                <span className="font-semibold text-gray-900">Connect to Nayax Kiosk</span>
              </div>
              <p className="text-sm text-gray-600">
                Connect your OCPI system to the Nayax Kiosk network for roaming services.
              </p>
            </button>

            {/* Connect to OCPI-to-VPOST */}
            <button
              type="button"
              onClick={() => setConnectionType('vpost')}
              className="flex flex-col items-start p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Server className="h-5 w-5 text-purple-600" />
                </div>
                <span className="font-semibold text-gray-900">Connect to OCPI-to-VPOST</span>
              </div>
              <p className="text-sm text-gray-600">
                Connect your OCPI system to the Nayax VPOST network.
              </p>
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Step 2b-vpost: External CPO VPOST form
  if (cpoType === 'external' && connectionType === 'vpost') {
    return (
      <ExternalVpostSetupForm
        onSubmit={onSubmitExternalVpost}
        onCancel={() => setConnectionType(null)}
        onLoadCsmsNames={onLoadCsmsNames}
        isLoading={isLoadingExternalVpost}
        error={error}
      />
    );
  }

  // Step 2b-kiosk: External CPO Kiosk form
  return (
    <Card className="border-blue-200 bg-blue-50/50">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <LinkIcon className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <CardTitle className="text-lg text-blue-900">Connect to Nayax Kiosk</CardTitle>
            <CardDescription className="text-blue-700">
              Connect your external OCPI system to the Nayax Kiosk network.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmitExternal} className="space-y-6" autoComplete="off">
          {/* Back Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setConnectionType(null)}
            className="text-blue-600 hover:text-blue-700 -ml-2"
          >
            ← Back to type selection
          </Button>

          {/* Information Box */}
          <div className="bg-white rounded-lg border border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  <strong>Versions URL:</strong> The OCPI versions endpoint URL of your external system (e.g., https://your-system.com/ocpi/versions).
                </p>
                <p>
                  <strong>Token A:</strong> The authentication token for your external OCPI system.
                </p>
                <p>
                  <strong>OCPI Version:</strong> The OCPI protocol version to use for communication.
                </p>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Versions URL */}
            <div className="space-y-2">
              <Label htmlFor="versionsUrl" className="text-gray-700">
                Versions URL <span className="text-red-500">*</span>
              </Label>
              <Input
                id="versionsUrl"
                name="ocpi-versions-url"
                autoComplete="off"
                value={versionsUrl}
                onChange={(e) => {
                  setVersionsUrl(e.target.value);
                  if (versionsUrlError) validateVersionsUrl(e.target.value);
                }}
                placeholder="https://your-system.com/ocpi/versions"
                className={versionsUrlError ? 'border-red-500' : ''}
                disabled={isLoadingExternal}
              />
              {versionsUrlError && <p className="text-xs text-red-600">{versionsUrlError}</p>}
              <p className="text-xs text-gray-500">The OCPI versions endpoint of your external system</p>
            </div>

            {/* Token A */}
            <div className="space-y-2">
              <Label htmlFor="tokenA" className="text-gray-700">
                Token A <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="tokenA"
                  name="ocpi-token-a"
                  type={showTokenA ? 'text' : 'password'}
                  autoComplete="off"
                  value={tokenA}
                  onChange={(e) => {
                    setTokenA(e.target.value);
                    if (tokenAError) validateTokenA(e.target.value);
                  }}
                  placeholder="Your authentication token"
                  className={`pr-10 ${tokenAError ? 'border-red-500' : ''}`}
                  disabled={isLoadingExternal}
                />
                <button
                  type="button"
                  onClick={() => setShowTokenA(!showTokenA)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={isLoadingExternal}
                >
                  {showTokenA ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {tokenAError && <p className="text-xs text-red-600">{tokenAError}</p>}
              <p className="text-xs text-gray-500">Authentication token for your external OCPI system</p>
            </div>

            {/* Connection Version */}
            <div className="space-y-2">
              <Label htmlFor="connectionVersion" className="text-gray-700">
                OCPI Version <span className="text-red-500">*</span>
              </Label>
              <Select
                value={connectionVersion}
                onValueChange={(value) => setConnectionVersion(value as OcpiVersion)}
                disabled={isLoadingExternal}
              >
                <SelectTrigger id="connectionVersion">
                  <SelectValue placeholder="Select OCPI version" />
                </SelectTrigger>
                <SelectContent>
                  {OCPI_VERSIONS.map((version) => (
                    <SelectItem key={version} value={version}>
                      OCPI {version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">The OCPI protocol version to use</p>
            </div>

            {/* CSMS — dropdown from GET /ocpi-admin/csms */}
            <div className="space-y-2">
              <Label htmlFor="kiosk-csmsName" className="text-gray-700">
                CSMS <span className="text-red-500">*</span>
              </Label>

              {/* Loading state */}
              {csmsNamesLoading && (
                <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-gray-50">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  <span className="text-sm text-gray-400">Loading CSMS list...</span>
                </div>
              )}

              {/* Error / unavailable state */}
              {csmsUnavailable && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 flex-1">{csmsNamesError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCsmsRetryKey((key) => key + 1)}
                    className="h-7"
                  >
                    Retry
                  </Button>
                </div>
              )}

              {/* Dropdown — only shown when names loaded successfully */}
              {!csmsNamesLoading && !csmsUnavailable && (
                <Select
                  value={csmsName}
                  onValueChange={(value) => {
                    setCsmsName(value);
                    if (csmsNameError) setCsmsNameError(null);
                  }}
                  disabled={isLoadingExternal}
                >
                  <SelectTrigger id="kiosk-csmsName" className={csmsNameError ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select a CSMS" />
                  </SelectTrigger>
                  <SelectContent>
                    {csmsNames.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {csmsNameError && <p className="text-xs text-red-600">{csmsNameError}</p>}
              <p className="text-xs text-gray-500">The CSMS to associate with this connection</p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button type="submit" disabled={isLoadingExternal || csmsNamesLoading || csmsUnavailable} className="bg-purple-600 hover:bg-purple-700 text-white">
              {isLoadingExternal ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 mr-2" />
                  Connect to Kiosk
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
